import {
    RaThemeOptions,
    nanoDarkTheme,
    nanoLightTheme,
} from 'react-admin';

import { AutoLimitPaper } from '../common/searchDropdown';

export type ThemeName = 'nano';

export type Theme = {
    name: ThemeName;
    light?: RaThemeOptions;
    dark?: RaThemeOptions;
    single?: RaThemeOptions;
};

/** Nano + Odoo shell: hide docked sidebar strip (navigation is in the top bar). */
const withOdooNanoSidebar = (theme: RaThemeOptions): RaThemeOptions => ({
    ...theme,
    sidebar: {
        ...((theme as RaThemeOptions & { sidebar?: object }).sidebar ?? {}),
        width: 280,
        closedWidth: 0,
    },
});

/**
 * Every dropdown search looks like the Products search (JobEstimatePartLineProductField):
 * compact 13px rows separated by dividers, light-blue hover/selected row, a shadowed paper and
 * no popup arrow. Behaviour: every Autocomplete that doesn't bring its own paper gets
 * AutoLimitPaper (first 8 rows + "Search more..."); react-admin pickers get the same through
 * SearchAutocompleteInput / CompactAutocompleteInput (common/searchDropdown).
 * Merged over nano's own MuiAutocomplete settings rather than replacing them.
 */
const withOdooSearchDropdowns = (theme: RaThemeOptions, mode: 'light' | 'dark'): RaThemeOptions => {
    const components = (theme.components ?? {}) as Record<string, { defaultProps?: object; styleOverrides?: object }>;
    const current = components.MuiAutocomplete ?? {};
    const rowHover = mode === 'dark' ? 'rgba(144,202,249,0.08)' : '#e8f4fd';
    return {
        ...theme,
        components: {
            ...theme.components,
            MuiAutocomplete: {
                ...current,
                defaultProps: {
                    ...(current.defaultProps ?? {}),
                    forcePopupIcon: false,
                    slots: { paper: AutoLimitPaper },
                },
                styleOverrides: {
                    ...(current.styleOverrides ?? {}),
                    paper: ({ theme: t }: { theme: { palette: { divider: string } } }) => ({
                        borderRadius: 4,
                        border: `1px solid ${t.palette.divider}`,
                        marginTop: 2,
                        overflow: 'hidden',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                    }),
                    listbox: {
                        paddingTop: 0,
                        paddingBottom: 0,
                        maxHeight: 320,
                    },
                    option: ({ theme: t }: { theme: { palette: { divider: string } } }) => ({
                        padding: '7px 12px',
                        fontSize: 13,
                        minHeight: 'unset',
                        borderBottom: `1px solid ${t.palette.divider}`,
                        '&:last-of-type': { borderBottom: 'none' },
                        '&:hover, &.Mui-focused, &[aria-selected="true"]': { backgroundColor: rowHover },
                    }),
                    noOptions: { fontSize: 13, padding: '7px 12px' },
                    loading: { fontSize: 13, padding: '7px 12px' },
                },
            },
        } as RaThemeOptions['components'],
    };
};

export const themes: Theme[] = [
    {
        name: 'nano',
        light: withOdooSearchDropdowns(withOdooNanoSidebar(nanoLightTheme), 'light'),
        dark: withOdooSearchDropdowns(withOdooNanoSidebar(nanoDarkTheme), 'dark'),
    },
];
